import {
  CheckBadgeIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline';
import type { MemberGroup } from '@library/types/memberGroups';
import { UserKebabMenu } from '@library/components/menus/kebabMenu/UserKebabMenu';
import type { UserRoleMenuActions } from '@library/components/menus/kebabMenu/UserKebabMenu';
import { formatDateFromYMD } from '@library/component-utils/dateTime';
import { useCallback } from 'react';

export interface MemberGroupsRowProps {
  memberGroup: MemberGroup;
  onRowClick: (id: number) => void;
  menuActions: UserRoleMenuActions;
}

export const MemberGroupsRow = ({
  memberGroup,
  onRowClick,
  menuActions
}: MemberGroupsRowProps) => {
  const { id } = memberGroup;

  const onClick = useCallback(() => {
    onRowClick(id);
  }, [id, onRowClick]);

  return (
    <div
      aria-hidden="true"
      className="border-soFresh-140 text-soClean-110 hover:bg-soFresh-120 group/user text-2xs relative flex h-16 cursor-pointer items-center border-b xl:text-xs"
      data-testid={`member-group-${id}`}
      onClick={onClick}
    >
      <p
        className="flex flex-1 items-center gap-2 p-3"
        data-testid={`member-group-${id}-name`}
      >
        <span>{memberGroup.name}</span>
        {memberGroup.vip && (
          <CheckBadgeIcon className="text-spottieOttie h-[1.5em]" title="VIP" />
        )}
        {memberGroup.prevent_lineups && (
          <ShieldExclamationIcon
            className="text-roses h-[1.5em]"
            title="Prevent lineups"
          />
        )}
      </p>
      <p className="flex flex-1 p-3" data-testid={`member-group-${id}-id`}>
        {memberGroup.id}
      </p>
      <p
        className="flex flex-1 p-3"
        data-testid={`member-group-${id}-description`}
      >
        {memberGroup.description}
      </p>
      <p
        className="flex flex-1 p-3"
        data-testid={`member-group-${id}-updated-at`}
      >
        {formatDateFromYMD(memberGroup.updated_at)}
      </p>
      <div className="absolute right-2">
        <UserKebabMenu
          editMessage="Edit member group"
          id={String(id)}
          menuActions={menuActions}
        />
      </div>
    </div>
  );
};
